export const SIGNATURE_REQUEST_STATUSES = ['pending', 'completed', 'declined', 'cancelled'];
export const SIGNER_STATUSES = ['pending', 'signed', 'declined'];
/** A guest link is valid this long; a reminder sends a fresh link (the old one stops working). */
export const SIGNATURE_GUEST_LINK_DAYS = 14;
/** Free-text phone → E.164 ("+41791234567"), or null when it has no country code. */
export function normalizePhoneE164(raw) {
    if (!raw)
        return null;
    let s = raw.replace(/[\s\-().\/]/g, '');
    if (s.startsWith('00'))
        s = `+${s.slice(2)}`;
    return /^\+[1-9]\d{7,14}$/.test(s) ? s : null;
}
/** "+41791234567" → "+41 •• ••• •• 67" (first two and last two digits only). */
export function maskPhone(e164) {
    return e164.length > 6 ? `${e164.slice(0, 3)} •• ••• •• ${e164.slice(-2)}` : '••••';
}
/** Field types placeable on the document. signature = the drawn/uploaded/typed
 *  image; initials = auto-derived from the signer's name (e.g. "ML");
 *  name/email/date/title/location auto-source from the signer (displayName, the
 *  signer's account email, signedAt, user.jobTitle, user.workLocation); text = free
 *  entry typed at signing time. */
export const SIGNATURE_FIELD_TYPES = ['signature', 'initials', 'name', 'email', 'date', 'title', 'location', 'text', 'stamp'];
/** The signer whose turn it is (ordered requests) — first pending in order. */
export function currentSigner(r) {
    return r.signers.find((s) => s.status === 'pending');
}
/** Whether this signer may sign NOW (parallel: any pending; ordered: their turn). */
export function isSignersTurn(r, email) {
    const e = email.toLowerCase();
    const me = r.signers.find((s) => s.email.toLowerCase() === e);
    if (!me || me.status !== 'pending')
        return false;
    if (!r.ordered)
        return true;
    return currentSigner(r)?.email.toLowerCase() === e;
}
/** Vault category the signed copies are filed under. */
export const SIGNED_DOCUMENTS_CATEGORY = 'Signed documents';
/** Max source PDF size (base64 travels through the callable). */
export const SIGNATURE_MAX_PDF_BYTES = 15 * 1024 * 1024;
/** Max signature image size. */
export const SIGNATURE_MAX_IMAGE_BYTES = 1 * 1024 * 1024;
export const SIGNATURE_MAX_SIGNERS = 10;
export function signatureRequestIsOpen(r) {
    return r.status === 'pending';
}
/** The caller's signer entry (email lowercased). */
export function signerFor(r, email) {
    const e = email.toLowerCase();
    return r.signers.find((s) => s.email.toLowerCase() === e);
}
/** Initials from a display name — first letter of the first + last word,
 *  uppercased (e.g. "Michael Leitner" → "ML", "Cher" → "C"). Used by the
 *  `initials` placed-field type (stamped) + the preview. */
export function signatureInitials(displayName) {
    const words = displayName.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return '';
    if (words.length === 1)
        return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}
// ===========================================================================
// Signature Templates
// ===========================================================================
/** How a placeholder's value is sourced at send time. */
export const TEMPLATE_PLACEHOLDER_SOURCES = ['manual', 'employeeName', 'employeeJobTitle', 'today'];
/** Whether a (non-template-admin) SENDER may see/use this template.
 *  Absent/empty allowedSenderEmails = available to everyone. */
export function templateAvailableToSender(t, email) {
    const list = t.allowedSenderEmails ?? [];
    return list.length === 0 || list.includes(email.toLowerCase());
}
// ===========================================================================
// Text-message confirmation for contracts (Oct 2026)
// ===========================================================================
/** The vault category whose documents always require the text-message code. */
export const CONTRACT_DOCUMENT_CATEGORY = 'Contract';
/** A phone number the person changed themselves counts only after this many days (HR-entered: at once). */
export const SMS_PHONE_COOLDOWN_DAYS = 7;
/** Whether a request requires every signer to confirm by text message. */
export function signatureRequiresSms(opts) {
    return opts.requested === true
        || opts.vaultCategory === CONTRACT_DOCUMENT_CATEGORY
        || (opts.signerCategories ?? []).some((c) => c === CONTRACT_DOCUMENT_CATEGORY);
}
/** Can this person's private phone receive the code now? */
export function smsPhoneStatus(u, now = new Date()) {
    const e164 = normalizePhoneE164(u.privatePhone);
    if (!e164)
        return { ok: false, reason: 'missing' };
    const masked = maskPhone(e164);
    if (u.privatePhoneChangedBy === 'self' && u.privatePhoneChangedAt) {
        const usable = new Date(Date.parse(u.privatePhoneChangedAt) + SMS_PHONE_COOLDOWN_DAYS * 864e5);
        if (usable > now)
            return { ok: false, reason: 'recent', e164, masked, usableFrom: usable.toISOString() };
    }
    return { ok: true, e164, masked };
}
//# sourceMappingURL=signatures.js.map